import NextAuth from "next-auth";

import Google from "next-auth/providers/google";
import GitHub from "next-auth/providers/github";
import Twitter from "next-auth/providers/twitter";
import CredentialsProvider from "next-auth/providers/credentials";
// import Discord from "next-auth/providers/discord";

import nacl from "tweetnacl";
import { PublicKey as SolPubKey } from "@solana/web3.js";
import { getBytes, hashMessage, recoverAddress } from "ethers";
import { IChain, AllChains } from "based-auth/chains";
import { saveSocialUser, saveWalletUser } from "./user-profile";
import { createKeypairs } from "./create-keypairs";
import { NetworkSymbols } from "based-auth/networks";
import { UserProfileModel } from "based-shared/models";
import { getUserProfile } from "based-shared/utils/get-user-profile";

if (!process.env.AUTH_GOOGLE_ID || !process.env.AUTH_GOOGLE_SECRET) {
  throw new Error("AUTH_GOOGLE_ID and AUTH_GOOGLE_SECRET must be set");
}
if (!process.env.AUTH_GITHUB_ID || !process.env.AUTH_GITHUB_SECRET) {
  throw new Error("AUTH_GITHUB_ID and AUTH_GITHUB_SECRET must be set");
}
if (!process.env.AUTH_TWITTER_ID || !process.env.AUTH_TWITTER_SECRET) {
  throw new Error("AUTH_TWITTER_ID and AUTH_TWITTER_SECRET must be set");
}

const authConfig = NextAuth({
  providers: [
    Twitter({
      version: "2.0",
      clientId: process.env.AUTH_TWITTER_ID,
      clientSecret: process.env.AUTH_TWITTER_SECRET,
      profile: async ({
        data: { profile_image_url, name, username },
      }: {
        data: {
          profile_image_url: string;
          name: string;
          username: string;
        };
      }) => {
        console.log({
          provider: "twitter",
          name,
          userid: username,
          image: profile_image_url,
        });
        return await saveSocialUser("twitter", username, profile_image_url);
      },
    } as any),
    Google({
      clientId: process.env.AUTH_GOOGLE_ID,
      clientSecret: process.env.AUTH_GOOGLE_SECRET,
      profile: async ({ email, picture }: any) => {
        const userid = email.split("@")[0];
        return await saveSocialUser("google", userid, picture);
      },
    }),
    GitHub({
      clientId: process.env.AUTH_GITHUB_ID,
      clientSecret: process.env.AUTH_GITHUB_SECRET,
      profile: async ({ name, login, avatar_url }) => {
        return await saveSocialUser("github", login, avatar_url);
      },
    }),

    CredentialsProvider({
      name: "Connect Wallet",
      credentials: {
        chainId: {
          label: "Chain Id",
          type: "number",
          placeholder: "1...",
        },
        address: {
          label: "Address",
          type: "text",
          placeholder: "0x0...",
        },
        message: {
          label: "Message",
          type: "text",
          placeholder: "hi...",
        },
        signature: {
          label: "Signature",
          type: "text",
          placeholder: "0x0...",
        },
      },
      async authorize(credentials: any) {
        if (
          !credentials.provider ||
          !credentials.chainId ||
          !credentials.address ||
          !credentials.message ||
          !credentials.signature
        ) {
          console.log("Missing credentials");
          return null;
        }
        const provider = credentials.provider;
        const chainId = Number(credentials.chainId);
        const address = credentials.address.toString();
        const message = credentials.message.toString();
        const signature = credentials.signature.toString();

        const chain = AllChains.find((c) => c.id === chainId);
        if (!chain) {
          console.log("Invalid chain", chainId);
          return null;
        }
        const network = chain.network.symbol;
        switch (network) {
          case "eth":
            const mesageHash = hashMessage(message);
            const recoveredAddress = recoverAddress(
              getBytes(mesageHash),
              signature
            );
            if (recoveredAddress.toLowerCase() !== address.toLowerCase()) {
              console.log("Invalid signature");
              return null;
            }
            break;
          case "sol":
            nacl.sign.detached.verify(
              new TextEncoder().encode(message),
              Buffer.from(signature, "base64"),
              new SolPubKey(address).toBytes()
            );
            break;
          default:
            throw new Error("Invalid network");
        }
        return await saveWalletUser(provider, chainId, network, address);
      },
    }),
  ],
  theme: {
    colorScheme: "dark",
  },
  callbacks: {
    async jwt({ token, user, trigger, session }) {
      if (trigger === "update" && session.id === "switchChain") {
        const chain = AllChains.find((c) => c.id === session.chainId) as IChain;
        if (!chain) {
          return token;
        }
        const originalChainId = token.chainId;
        const originalAddress = token.address;
        const originalNetwork = token.network;

        token.chainId = chain.id;
        token.network = chain.network.symbol;
        if (!token.social) {
          return token;
        }

        const { address } = createKeypairs(
          token.network as NetworkSymbols,
          token.social as string,
          token.provider as string
        );

        const user = await UserProfileModel.findOne({
          "users.address": originalAddress,
          "users.network": originalNetwork,
        });
        if (!user) {
          token.chainId = originalChainId;
          token.network = originalNetwork;
          return token;
        }

        user.users.push({
          address,
          network: chain.network.symbol,
          social: token.social as string,
          provider: token.provider as string,
        });
        await user.save();

        token.address = address;
        token.multipleUsers = user.users.length > 1;

        return token;
      } else if (trigger === "update" && session.id === "switchAccount") {
        const user = await getUserProfile(session.address, session.network);
        const users = user?.users || [];
        const selectedUser = users.find(
          (u: { network: string; address: string }) =>
            u.network === session.network && u.address === session.address
        ) as any;
        if (selectedUser) {
          token.network = selectedUser.network;
          token.address = selectedUser.address;
          token.social = selectedUser.social;
          token.name = selectedUser.name;
          token.image = selectedUser.image;
          token.role = selectedUser.role;
          token.provider = selectedUser.provider;
        }
        return token;
      }
      if (user) {
        return { ...token, ...user };
      }
      return token;
    },
    async session({ session, token }) {
      session.user = {
        id: token.id ? String(token.id) : "",
        name: typeof token.name === "string" ? token.name : undefined,
        chainId: typeof token.chainId === "number" ? token.chainId : undefined,
        network: typeof token.network === "string" ? token.network : undefined,
        address: typeof token.address === "string" ? token.address : undefined,
        provider:
          typeof token.provider === "string" ? token.provider : undefined,
        image: typeof token.image === "string" ? token.image : undefined,
        social: typeof token.social === "string" ? token.social : undefined,
        role: typeof token.role === "string" ? token.role : undefined,
      } as any;
      return session;
    },
  },
  session: { strategy: "jwt", maxAge: 86400 },
  secret: process.env.AUTH_SECRET!,
});

export const { handlers, signIn, signOut } = authConfig;

export const auth: any = authConfig.auth;

export default authConfig;
